import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { useEvents } from '../../contexts/EventContext';
import { useConfig } from '../../contexts/ConfigContext';
import { STORAGE_KEYS } from '../../utils/constants';
import { formatDate } from '../../utils/helpers';
import { getCategoryColor, getCategoryLabel, RELATIONSHIP_LABELS, Relationship } from '../../types';
import { photoStatusLabel } from '../../utils/attendeeExport';
import api from '../../services/api';

// D5 — cap the admin check-in content to a comfortable working width so the
// Check In actions sit next to each attendee on a laptop instead of being
// flung to the far screen edge (MainLayout gives admin pages no max-width).
const CHECKIN_MAX_WIDTH = 820;

interface RegistrationAttendee {
  id: string;
  familyMemberId: string | null;
  familyMember: {
    id: string;
    name: string;
    relationship: string;
    // Phase 0 — per-child photo consent (from CRM via the register include).
    photoPermission?: boolean;
  } | null;
  // Plan 4.10 — XOR with familyMember. Guest rows populate eventGuest.
  eventGuestId?: string | null;
  eventGuest?: {
    id: string;
    firstName: string;
    lastName: string;
    relationship: string;
    relationshipOther?: string | null;
    isRedacted?: boolean;
  } | null;
  checkedIn: boolean;
  checkedInAt?: string;
}

interface EventRegistration {
  id: string;
  attendeeCount: number;
  attendees?: RegistrationAttendee[];
  status: string;
  checkedIn?: boolean;
  checkedInAt?: string | null;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const CheckInPage: React.FC = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { events } = useEvents();
  const { features } = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // QR scanning is available only on mobile AND when the qrCheckIn feature is on.
  // When off, the page falls back to the manual check-in list on every device.
  const qrScanEnabled = features.qrCheckIn && isMobile;

  // On desktop, default to manual list (tab 1), on mobile default to QR scanner (tab 0)
  const [tab, setTab] = useState(isMobile ? 0 : 1);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  // Phase 0 — at-door refresh: true when the last re-pull failed but we're still
  // showing the previously-loaded (last-synced) attendee state.
  const [refreshError, setRefreshError] = useState(false);
  const [everLoaded, setEverLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [scannerReady, setScannerReady] = useState(false);
  const scannerRef = useRef<any>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);

  const event = events.find((e) => e.id === eventId);

  const fetchRegistrations = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await api.get(`/registrations/event/${eventId}`);
      setRegistrations(res.data);
      setRefreshError(false);
      setEverLoaded(true);
    } catch {
      // Fail-closed refresh: if we've shown data before, KEEP it (last-synced)
      // and flag that this re-pull failed, rather than blanking the register at
      // the door. Only clear when we never had anything to show.
      setRefreshError(true);
      setRegistrations((prev) => (prev.length > 0 ? prev : []));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  // Check in all attendees for a registration
  const handleCheckInAll = useCallback(async (registrationId: string) => {
    try {
      const res = await api.put(`/registrations/${registrationId}/check-in`);
      setSnackbar({
        open: true,
        message: `${res.data.user?.firstName} ${res.data.user?.lastName} - all attendees checked in!`,
        severity: 'success',
      });
      fetchRegistrations();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Check-in failed';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  }, [fetchRegistrations]);

  // Check in a single attendee
  const handleCheckInAttendee = async (attendeeId: string, attendeeName: string) => {
    try {
      await api.put(`/registrations/attendee/${attendeeId}/check-in`);
      setSnackbar({
        open: true,
        message: `${attendeeName} checked in successfully!`,
        severity: 'success',
      });
      fetchRegistrations();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Check-in failed';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  // C6 un-check-in: undo a mistaken check-in for a single attendee
  const handleUnCheckInAttendee = async (attendeeId: string, attendeeName: string) => {
    try {
      await api.put(`/registrations/attendee/${attendeeId}/uncheck-in`);
      setSnackbar({ open: true, message: `${attendeeName} check-in undone`, severity: 'success' });
      fetchRegistrations();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Undo failed';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  // C6 un-check-in: undo check-in for a whole registration
  const handleUnCheckInAll = useCallback(async (registrationId: string) => {
    try {
      await api.put(`/registrations/${registrationId}/uncheck-in`);
      setSnackbar({ open: true, message: 'All check-ins undone', severity: 'success' });
      fetchRegistrations();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Undo failed';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  }, [fetchRegistrations]);

  // Handle QR code scan result
  const handleScanResult = useCallback(async (decodedText: string) => {
    // UUID format check
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(decodedText)) {
      setSnackbar({ open: true, message: 'Invalid QR code', severity: 'error' });
      return;
    }

    // Try attendee check-in first (per-attendee QR codes)
    try {
      const res = await api.put(`/registrations/attendee/${decodedText}/check-in`);
      setSnackbar({
        open: true,
        message: `${res.data.familyMember?.name || 'Attendee'} checked in!`,
        severity: 'success',
      });
      fetchRegistrations();
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Not an attendee ID - try as registration ID (backward compat)
        handleCheckInAll(decodedText);
      } else {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || 'Check-in failed',
          severity: 'error',
        });
      }
    }
  }, [fetchRegistrations, handleCheckInAll]);

  // QR Scanner setup - Only on mobile, uses rear camera by default
  useEffect(() => {
    if (!qrScanEnabled || tab !== 0 || !scannerContainerRef.current) {
      // Clean up scanner when switching away
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
        setScannerReady(false);
      }
      return;
    }

    let mounted = true;

    const startScanner = async (scanner: any, cameraConfig: any) => {
      await scanner.start(
        cameraConfig,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText: string) => {
          handleScanResult(decodedText);
        },
        () => {
          // Scan error - ignore (happens on every failed frame)
        },
      );

      // Store device ID from the active video track (no extra permission request)
      try {
        const videoElement = scannerContainerRef.current?.querySelector('video');
        const track = videoElement?.srcObject instanceof MediaStream
          ? videoElement.srcObject.getVideoTracks()[0]
          : null;
        const deviceId = track?.getSettings()?.deviceId;
        if (deviceId) {
          localStorage.setItem(STORAGE_KEYS.CAMERA_DEVICE_ID, deviceId);
        }
      } catch {
        // Ignore - device ID storage is best-effort
      }

      scannerRef.current = scanner;
      setScannerReady(true);
    };

    const initScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted || !scannerContainerRef.current) return;

        const scanner = new Html5Qrcode('qr-reader');
        const storedDeviceId = localStorage.getItem(STORAGE_KEYS.CAMERA_DEVICE_ID);

        try {
          const cameraConfig: any = storedDeviceId
            ? { deviceId: { exact: storedDeviceId } }
            : { facingMode: 'environment' };
          await startScanner(scanner, cameraConfig);
        } catch {
          // Stored device ID may be stale - fall back to facingMode
          if (storedDeviceId) {
            localStorage.removeItem(STORAGE_KEYS.CAMERA_DEVICE_ID);
            await startScanner(scanner, { facingMode: 'environment' });
          } else {
            throw new Error('Camera start failed');
          }
        }
      } catch {
        setSnackbar({ open: true, message: 'Failed to start camera', severity: 'error' });
      }
    };

    initScanner();

    return () => {
      mounted = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
        setScannerReady(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, qrScanEnabled, handleScanResult]);

  const confirmedRegistrations = registrations.filter((r) => r.status === 'confirmed');

  // Count individual attendees checked in
  const totalAttendees = confirmedRegistrations.reduce((sum, r) => {
    return sum + (r.attendees?.length || r.attendeeCount);
  }, 0);
  const checkedInCount = confirmedRegistrations.reduce((sum, r) => {
    if (r.attendees && r.attendees.length > 0) {
      return sum + r.attendees.filter(a => a.checkedIn).length;
    }
    return sum + (r.checkedIn ? r.attendeeCount : 0);
  }, 0);

  return (
    <Box sx={{ maxWidth: CHECKIN_MAX_WIDTH, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Event Check-in
      </Typography>

      {event && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Chip
                    label={getCategoryLabel(event.category)}
                    size="small"
                    sx={{ backgroundColor: getCategoryColor(event.category), color: 'white' }}
                  />
                  <Typography variant="h6">{event.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(event.date)} | {event.location}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h4" color="success.main" fontWeight={700}>
                  {checkedInCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of {totalAttendees} attendees checked in
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Only show tabs on mobile where QR scanning is available */}
      {qrScanEnabled ? (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab icon={<QrCodeScannerIcon />} label="QR Scanner" />
          <Tab label="Manual List" />
        </Tabs>
      ) : (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Use the list below to manually check in attendees. QR scanning is available on mobile devices.
        </Typography>
      )}

      {/* Phase 0 — at-door refresh: pull the latest photo/consent state before the
          door, and if the pull fails, make clear the shown state may be stale. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button size="small" variant="outlined" onClick={fetchRegistrations} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh permissions'}
        </Button>
        {refreshError && everLoaded && (
          <Alert severity="warning" sx={{ py: 0, flexGrow: 1 }}>
            Couldn't refresh — showing last synced permissions.
          </Alert>
        )}
      </Box>

      {/* QR Scanner Tab - Only on mobile, when QR check-in is enabled */}
      {qrScanEnabled && tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Point the camera at a family's QR code to check them in
            </Typography>
            <Box
              ref={scannerContainerRef}
              sx={{ maxWidth: 500, mx: 'auto' }}
            >
              <div id="qr-reader" />
              {!scannerReady && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Manual List Tab - Always show unless the QR scanner tab is active */}
      {(!qrScanEnabled || tab === 1) && (
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : confirmedRegistrations.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No confirmed registrations for this event
              </Typography>
            ) : (
              <List>
                {confirmedRegistrations.map((reg) => {
                  const attendees = reg.attendees || [];
                  const allCheckedIn = attendees.length > 0
                    ? attendees.every(a => a.checkedIn)
                    : reg.checkedIn;
                  const checkedInAttendeeCount = attendees.filter(a => a.checkedIn).length;

                  return (
                    <ListItem
                      key={reg.id}
                      divider
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        backgroundColor: allCheckedIn ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
                        py: 2,
                      }}
                    >
                      {/* Family header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GroupIcon color="action" />
                          <Typography fontWeight={600}>
                            {reg.user.firstName} {reg.user.lastName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {attendees.length > 0 && !allCheckedIn && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleCheckInAll(reg.id)}
                            >
                              Check In All ({attendees.length - checkedInAttendeeCount})
                            </Button>
                          )}
                          {allCheckedIn && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="All Checked In"
                              size="small"
                              color="success"
                            />
                          )}
                          {/* C6 un-check-in: undo the whole registration when anyone is checked in */}
                          {checkedInAttendeeCount > 0 && (
                            <Button
                              variant="text"
                              size="small"
                              color="warning"
                              onClick={() => handleUnCheckInAll(reg.id)}
                            >
                              Undo All
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {/* Attendee list */}
                      {attendees.length > 0 ? (
                        <Box sx={{ pl: 4 }}>
                          {attendees.map((attendee) => (
                            <Box
                              key={attendee.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.75,
                                borderBottom: '1px solid #eee',
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              {(() => {
                                const isGuest = !!attendee.eventGuest;
                                const name = attendee.familyMember
                                  ? attendee.familyMember.name
                                  : attendee.eventGuest
                                    ? `${attendee.eventGuest.firstName} ${attendee.eventGuest.lastName}`.trim()
                                    : 'Unknown';
                                const relLabel = attendee.familyMember
                                  ? RELATIONSHIP_LABELS[attendee.familyMember.relationship as Relationship] ||
                                    attendee.familyMember.relationship
                                  : attendee.eventGuest
                                    ? `${attendee.eventGuest.relationship}${isGuest ? ' (Guest)' : ''}`
                                    : '';
                                return (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <PersonIcon fontSize="small" color="action" />
                                      <Typography variant="body2">{name}</Typography>
                                      <Chip
                                        label={relLabel}
                                        size="small"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                      {/* Phase 0 — fail-closed photo permission: only a
                                          positively-confirmed true shows "Photos OK". */}
                                      <Chip
                                        label={photoStatusLabel(attendee.familyMember?.photoPermission)}
                                        size="small"
                                        color={attendee.familyMember?.photoPermission === true ? 'success' : 'warning'}
                                        variant={attendee.familyMember?.photoPermission === true ? 'filled' : 'outlined'}
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                    </Box>
                                    {attendee.checkedIn ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <Chip
                                          icon={<CheckCircleIcon />}
                                          label="Checked In"
                                          size="small"
                                          color="success"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                        <Button
                                          variant="text"
                                          size="small"
                                          color="warning"
                                          onClick={() => handleUnCheckInAttendee(attendee.id, name)}
                                          sx={{ minWidth: 'auto', px: 1 }}
                                        >
                                          Undo
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleCheckInAttendee(attendee.id, name)}
                                        sx={{ minWidth: 'auto', px: 1.5, flexShrink: 0 }}
                                      >
                                        Check In
                                      </Button>
                                    )}
                                  </>
                                );
                              })()}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        // Fallback for registrations without attendee details
                        <Box sx={{ pl: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {reg.attendeeCount} attendee{reg.attendeeCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckInPage;
